import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Wallet, CheckCircle, Clock, Plus, Calendar, ChevronDown, ChevronUp, History, Tag, Edit, Trash2 } from 'lucide-react';
import { billingApi, BillingSummaryDto, PaymentPlanDto, handleApiError, formatCurrency, formatDate, getPaymentPlanStatusColor } from '../../services/paymentsService';
import { discountsApi, DiscountDto } from '../../services/discountsService';
import { useAuth } from '../../contexts/AuthContext';
import { hexToRgba } from '../../utils/colorUtils';
import SummaryCard from '../billing/SummaryCard';
import ChargeBreakdown from '../billing/ChargeBreakdown';
import AgentCommissionOwed from '../billing/AgentCommissionOwed';
import PaymentHistory from '../billing/PaymentHistory';
import PaymentPlanSection from '../billing/PaymentPlanSection';
import RecordPaymentModal from '../modals/RecordPaymentModal';
import CreatePaymentPlanModal from '../modals/CreatePaymentPlanModal';
import DiscountFormModal from '../payments/DiscountFormModal';
import ConfirmDialog from '../ConfirmDialog';
import IconButton from '../common/IconButton';

interface BillingTabProps {
  clientId: number;
  clientName: string;
}

const Container = styled.div`
  direction: rtl;
  padding: 24px;

  @media (max-width: 767px) {
    padding: 16px;
  }
`;

const SummarySection = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const ActionsBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: var(--primary-light);
          color: var(--primary-color);
          &:hover {
            background: var(--primary-color);
            color: white;
          }
        `;
      case 'secondary':
        return `
          background: var(--background-secondary);
          color: var(--text-primary);
          &:hover { background: var(--hover-background); }
        `;
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover { background: var(--hover-background); }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SectionSeparator = styled.div`
  height: 1px;
  background: var(--border-color);
  margin: 32px 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  font-size: 16px;
  color: var(--text-secondary);
`;

const ErrorMessage = styled.div`
  background: var(--error-light);
  color: #dc2626;
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 20px;
  border: 1px solid #dc2626;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px;
`;

const EmptyStateIcon = styled.div`
  margin-bottom: 16px;
  color: var(--text-secondary);
  opacity: 0.3;
`;

const EmptyStateTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
`;

const EmptyStateMessage = styled.p`
  margin: 0 0 24px 0;
  font-size: 14px;
  color: var(--text-secondary);
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

// Previous Payment Plans Section - Collapsible
const PreviousPlansSection = styled.div`
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  margin-top: 24px;
`;

const PreviousPlansHeader = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: var(--background-secondary);
  border-radius: ${props => props.$isExpanded ? 'var(--border-radius-md) var(--border-radius-md) 0 0' : 'var(--border-radius-md)'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--hover-background);
  }
`;

const PreviousPlansTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
`;

const PreviousPlansCount = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: var(--text-secondary);
  background: var(--border-color);
  padding: 2px 8px;
  border-radius: 10px;
`;

const ChevronWrapper = styled.div`
  color: var(--text-secondary);
`;

const PreviousPlansContent = styled.div<{ $isExpanded: boolean }>`
  max-height: ${props => props.$isExpanded ? '500px' : '0'};
  overflow: ${props => props.$isExpanded ? 'auto' : 'hidden'};
  transition: max-height 0.3s ease;
`;

const PreviousPlanCard = styled.div`
  padding: 16px;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
`;

const PlanInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PlanMainInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PlanAmount = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  direction: ltr;
`;

const PlanStatusBadge = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: var(--border-radius-sm);
  font-size: 11px;
  font-weight: 600;
  background: ${props => hexToRgba(props.$color, 0.12)};
  color: ${props => props.$color};
`;

const PlanDetails = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  display: flex;
  gap: 16px;
`;

const PlanNotes = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  font-style: italic;
  max-width: 300px;
  text-align: left;

  @media (max-width: 767px) {
    text-align: right;
    max-width: 100%;
  }
`;

// Discounts list rows (within the collapsible discounts section)
const DiscountRow = styled.div`
  padding: 12px 16px;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const DiscountInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DiscountMain = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DiscountAmount = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  direction: ltr;
`;

const DiscountBadge = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: var(--border-radius-sm);
  font-size: 11px;
  font-weight: 600;
  background: ${props => hexToRgba(props.$color, 0.12)};
  color: ${props => props.$color};
`;

const DiscountDetails = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const DiscountRowActions = styled.div`
  display: flex;
  gap: 4px;
`;

const BillingTab: React.FC<BillingTabProps> = ({ clientId, clientName }) => {
  const { canViewClientFinance } = useAuth();

  // פיצ'ר ההנחות זמין לניהול למי שרשאי לצפות בנתוני חיוב של לקוח
  const canManageDiscounts = canViewClientFinance;

  const [summary, setSummary] = useState<BillingSummaryDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showRecordPaymentModal, setShowRecordPaymentModal] = useState(false);
  const [showCreatePaymentPlanModal, setShowCreatePaymentPlanModal] = useState(false);
  const [isPreviousPlansExpanded, setIsPreviousPlansExpanded] = useState(false);

  // Discounts (Payments Management → הנחות) — נשאבות מתוך ה-summary
  const [isDiscountsExpanded, setIsDiscountsExpanded] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState<DiscountDto | null>(null);
  const [deleteDiscountTarget, setDeleteDiscountTarget] = useState<DiscountDto | null>(null);
  const [deletingDiscount, setDeletingDiscount] = useState(false);

  const loadBillingSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await billingApi.getSummary(clientId);
      setSummary(data);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const handleAddDiscount = () => {
    setEditingDiscount(null);
    setShowDiscountModal(true);
  };

  const handleEditDiscount = (discount: DiscountDto) => {
    setEditingDiscount(discount);
    setShowDiscountModal(true);
  };

  const handleConfirmDeleteDiscount = async () => {
    if (!deleteDiscountTarget) return;
    try {
      setDeletingDiscount(true);
      await discountsApi.remove(deleteDiscountTarget.id);
      setDeleteDiscountTarget(null);
      loadBillingSummary();
    } finally {
      setDeletingDiscount(false);
    }
  };

  useEffect(() => {
    loadBillingSummary();
  }, [loadBillingSummary]);

  if (loading) {
    return (
      <Container>
        <LoadingContainer>טוען נתוני חיוב...</LoadingContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>{error}</ErrorMessage>
        <Button onClick={loadBillingSummary}>נסה שנית</Button>
      </Container>
    );
  }

  // Show empty state only if no billing record exists at all
  if (!summary || !summary.billingRecord) {
    return (
      <Container>
        <EmptyState>
          <EmptyStateIcon>
            <Wallet size={64} strokeWidth={1.5} />
          </EmptyStateIcon>
          <EmptyStateTitle>טרם הוזן מידע על חיובים</EmptyStateTitle>
          <EmptyStateMessage>
            רשומת חיוב תיווצר אוטומטית לאחר אישור החלטת ועדה עם סכומי גמלה
          </EmptyStateMessage>
        </EmptyState>
        {/* P9a — עמלות חתימה חבות לכל לקוח שחתם, גם לפני אישור ועדה. עמלת האישור תופיע רק לאחר אישור. */}
        {canViewClientFinance && <AgentCommissionOwed clientId={clientId} />}
      </Container>
    );
  }

  const hasBilling = summary.billingRecord !== null;
  const hasActivePaymentPlan = summary.paymentPlan !== null && summary.paymentPlan !== undefined;

  // הנחות: ההנחה נחשבת כתשלום ומקטינה את היתרה לתשלום בפועל.
  // היתרה האפקטיבית (לאחר הנחה) משמשת לתצוגה, לגידור כפתור "יצירת תוכנית" ולהצעת סכום התוכנית.
  const clientDiscounts = summary.discounts ?? [];
  const showDiscount = summary.totalDiscount > 0;
  const effectiveBalance = summary.balanceAfterDiscount;
  const displayBalance = effectiveBalance;
  const hasBalance = effectiveBalance > 0;

  return (
    <Container>
      {/* 1. Summary Cards */}
      <SummarySection>
        <SummaryCard
          title={'סך הכל לחיוב (כולל מע"מ)'}
          amount={summary.totalWithVat}
          color="var(--primary-color)"
          icon={Wallet}
        />
        <SummaryCard
          title="שולם עד כה"
          amount={summary.totalPaid}
          color="#16a34a"
          icon={CheckCircle}
        />
        {showDiscount && (
          <SummaryCard
            title="הנחות"
            amount={summary.totalDiscount}
            color="#9333ea"
            icon={Tag}
          />
        )}
        <SummaryCard
          title="יתרה לתשלום"
          amount={displayBalance}
          color={displayBalance > 0 ? '#d97706' : 'var(--text-secondary)'}
          icon={Clock}
        />
      </SummarySection>

      {/* 2. Charge Breakdown */}
      <ChargeBreakdown
        breakdown={summary.breakdown}
        billingId={summary.billingRecord?.id}
        canEdit={canManageDiscounts}
        onMonthsUpdated={loadBillingSummary}
      />

      {/* 2b. P9a — עמלות סוכן/ארגון חבות (חישוב בלבד; מגודר פיננסי). */}
      {canViewClientFinance && <AgentCommissionOwed clientId={clientId} />}

      {/* 3. Action Buttons */}
      {hasBilling && (
        <ActionsBar>
          <Button $variant="primary" onClick={() => setShowRecordPaymentModal(true)}>
            <Plus size={18} />
            רישום תשלום
          </Button>
          {hasBalance && !hasActivePaymentPlan && (
            <Button $variant="secondary" onClick={() => setShowCreatePaymentPlanModal(true)}>
              <Calendar size={18} />
              יצירת תוכנית תשלום
            </Button>
          )}
          {canManageDiscounts && (
            <Button $variant="secondary" onClick={handleAddDiscount}>
              <Tag size={18} />
              הוספת הנחה
            </Button>
          )}
        </ActionsBar>
      )}

      {/* Separator before Payment Plan */}
      {hasActivePaymentPlan && <SectionSeparator />}

      {/* 4. Payment Plan Section (combined - if exists) */}
      {summary.paymentPlan && (
        <PaymentPlanSection
          paymentPlan={summary.paymentPlan}
          onPlanChanged={loadBillingSummary}
        />
      )}

      {/* Separator before History */}
      <SectionSeparator />

      {/* 5. Payment History (collapsible) */}
      <PaymentHistory payments={summary.payments} discounts={clientDiscounts} />

      {/* 6. Previous Payment Plans (collapsible) */}
      {summary.previousPaymentPlans && summary.previousPaymentPlans.length > 0 && (
        <PreviousPlansSection>
          <PreviousPlansHeader
            $isExpanded={isPreviousPlansExpanded}
            onClick={() => setIsPreviousPlansExpanded(!isPreviousPlansExpanded)}
          >
            <PreviousPlansTitle>
              <History size={16} />
              תוכניות תשלום קודמות
              <PreviousPlansCount>{summary.previousPaymentPlans.length}</PreviousPlansCount>
            </PreviousPlansTitle>
            <ChevronWrapper>
              {isPreviousPlansExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </ChevronWrapper>
          </PreviousPlansHeader>

          <PreviousPlansContent $isExpanded={isPreviousPlansExpanded}>
            {summary.previousPaymentPlans.map((plan: PaymentPlanDto) => (
              <PreviousPlanCard key={plan.id}>
                <PlanInfo>
                  <PlanMainInfo>
                    <PlanAmount>{formatCurrency(plan.totalAmount)}</PlanAmount>
                    <PlanStatusBadge $color={getPaymentPlanStatusColor(plan.status)}>
                      {plan.statusName}
                    </PlanStatusBadge>
                  </PlanMainInfo>
                  <PlanDetails>
                    <span>{plan.numberOfInstallments} תשלומים</span>
                    <span>שולם: {formatCurrency(plan.amountPaid)}</span>
                    <span>נוצר: {formatDate(plan.createdAt)}</span>
                  </PlanDetails>
                </PlanInfo>
                {plan.notes && (
                  <PlanNotes>{plan.notes}</PlanNotes>
                )}
              </PreviousPlanCard>
            ))}
          </PreviousPlansContent>
        </PreviousPlansSection>
      )}

      {/* 7. Discounts (collapsible) — מנהל מערכת / מנהל תיקים / מזכירה, כשהפיצ'ר דלוק */}
      {canManageDiscounts && clientDiscounts.length > 0 && (
        <PreviousPlansSection>
          <PreviousPlansHeader
            $isExpanded={isDiscountsExpanded}
            onClick={() => setIsDiscountsExpanded(!isDiscountsExpanded)}
          >
            <PreviousPlansTitle>
              <Tag size={16} />
              הנחות
              <PreviousPlansCount>{clientDiscounts.length}</PreviousPlansCount>
            </PreviousPlansTitle>
            <ChevronWrapper>
              {isDiscountsExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </ChevronWrapper>
          </PreviousPlansHeader>

          <PreviousPlansContent $isExpanded={isDiscountsExpanded}>
            {clientDiscounts.map((discount) => (
              <DiscountRow key={discount.id}>
                <DiscountInfo>
                  <DiscountMain>
                    <DiscountAmount>{formatCurrency(discount.amount)}</DiscountAmount>
                    <DiscountBadge $color={discount.type === 2 ? '#9333ea' : '#64748b'}>
                      {discount.typeName}
                    </DiscountBadge>
                  </DiscountMain>
                  <DiscountDetails>
                    <span>תאריך: {formatDate(discount.discountDate)}</span>
                    {discount.type === 2 && (
                      <span>סיום: {discount.endDate ? formatDate(discount.endDate) : 'עד סוף התשלומים'}</span>
                    )}
                    <span>הנחה אפקטיבית: {formatCurrency(discount.effectiveAmount)}</span>
                    {discount.notes && <span>{discount.notes}</span>}
                  </DiscountDetails>
                </DiscountInfo>
                <DiscountRowActions>
                  <IconButton icon={Edit} title="עריכה" variant="primary" onClick={() => handleEditDiscount(discount)} />
                  <IconButton icon={Trash2} title="מחיקה" variant="danger" onClick={() => setDeleteDiscountTarget(discount)} />
                </DiscountRowActions>
              </DiscountRow>
            ))}
          </PreviousPlansContent>
        </PreviousPlansSection>
      )}

      {/* Modals */}
      {showRecordPaymentModal && (
        <RecordPaymentModal
          isOpen={showRecordPaymentModal}
          onClose={() => setShowRecordPaymentModal(false)}
          onSuccess={() => {
            setShowRecordPaymentModal(false);
            loadBillingSummary();
          }}
          clientId={clientId}
          clientName={clientName}
        />
      )}

      {showCreatePaymentPlanModal && (
        <CreatePaymentPlanModal
          isOpen={showCreatePaymentPlanModal}
          onClose={() => setShowCreatePaymentPlanModal(false)}
          onSuccess={() => {
            setShowCreatePaymentPlanModal(false);
            loadBillingSummary();
          }}
          clientId={clientId}
          clientName={clientName}
          suggestedAmount={effectiveBalance}
        />
      )}

      {showDiscountModal && (
        <DiscountFormModal
          isOpen={showDiscountModal}
          onClose={() => setShowDiscountModal(false)}
          onSaved={loadBillingSummary}
          clientId={clientId}
          discount={editingDiscount}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleteDiscountTarget}
        onClose={() => setDeleteDiscountTarget(null)}
        onConfirm={handleConfirmDeleteDiscount}
        title="מחיקת הנחה"
        message={
          deleteDiscountTarget
            ? `האם למחוק את ההנחה בסך ${formatCurrency(deleteDiscountTarget.amount)}?`
            : ''
        }
        confirmText="מחק"
        cancelText="ביטול"
        type="danger"
        loading={deletingDiscount}
      />
    </Container>
  );
};

export default BillingTab;
